import { InvalidStorageValue } from '../../../errors/InvalidStorageValue';
import type { ObservableState } from '../observable.types';

/**
 * Creates an observable state object that allows subscribing to immutable state changes.
 *
 * This function creates a state management system with the following capabilities:
 * - Get the current state
 * - Update state partially
 * - Subscribe to state changes
 * - Unsubscribe from state changes
 *
 * @param getInitialState - A function that returns the initial state object
 * @returns An ObservableState object with methods to manage and observe state changes
 * @example
 * ```typescript
 * const state = createObservableState(() => ({
 *   count: 0,
 *   name: 'John'
 * }));
 *
 * // Subscribe to changes
 * const unsubscribe = state.subscribe((current, previous) => {
 *   console.log('State changed:', current, 'Previous:', previous);
 * });
 *
 * // Update state
 * state.set({ count: 1 });
 *
 * // Get current state
 * const currentState = state.get();
 *
 * // Unsubscribe when done
 * unsubscribe();
 * ```
 */
export const createObservableState = <T extends Record<string, unknown>>(
  getInitialState: () => T
): ObservableState<T> => {
  // ===============
  // NOTIFICATIONS
  // ===============
  const subscribers = new Set<(value: T, previous: T) => void>();

  const notifySubscribers = (value: T, previous: T) => {
    subscribers.forEach((callback) => callback(value, previous));
  };

  const subscribe = (
    callback: (value: Readonly<T>, previous: Readonly<T>) => void
  ) => {
    subscribers.add(callback);

    return () => unsubscribe(callback);
  };

  const unsubscribe = (
    callback: (value: Readonly<T>, previous: Readonly<T>) => void
  ) => {
    subscribers.delete(callback);
  };

  // ===============
  // STATE
  // ===============

  let currentState = getInitialState();

  const get = () => currentState;

  const set = (partial: Partial<T>) => {
    const previous = currentState;

    Object.entries(partial).forEach(([key, value]) => {
      if (value === undefined) {
        throw new InvalidStorageValue(key, value);
      }
    });

    currentState = Object.assign({}, currentState, partial);

    notifySubscribers(currentState, previous);
  };

  return {
    get,
    getInitialState,
    set,
    subscribe,
    unsubscribe,
  };
};
